/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment-variables';
import { Attachment } from '../models/Attachment';
import { Attachments } from './Attachments';
import { AbstractFileAttachApi } from './AbstractFileAttachApi';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { FirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/cloud-functions.service';

export class ProjectFileAttachApi extends AbstractFileAttachApi {
	/** NOTE! DO NOT USE THIS DIRECTLY! Use the one from attached-file.service */
	public file$: BehaviorSubject<Attachment[]>; // all attachments
	public renderTrigger$: Subject<string>; // used to notify file viewers when upload completed or url fetched
	private destroy$: Subject<void>;

	private logger: ILoggerService = LoggerServiceFactory.create('ProjectFileAttachApi');

	constructor(
		private fbCloudFunctionsService: FirebaseCloudFunctionsService,
		private attachments: Attachments
	) {
		super();
		this.renderTrigger$ = new Subject();
		this.destroy$ = new Subject();
		this.file$ = new BehaviorSubject<Attachment[]>([]);
		attachments.data.list$
			.pipe(
				map((data: Map<string, Attachment>) => Array.from(data.values())),
				takeUntil(this.destroy$)
			)
			.subscribe(this.file$);
	}

	/**
	 * Sets/updates the "fileUrl"field for the given attachments.
	 */
	public updateFileUrls(data: Attachment[]): void {
		for (const file of data) {
			this.getImageUrl(file);
		}
	}

	// Retrieve image url based on given type
	private getImageUrl(attachment: Attachment): void {
		const prop = 'fileUrl';
		const blob = attachment.view.file?.gcsBlob;
		// no-op if blob is somehow undefined
		if (!blob) {
			return;
		}
		// fetch when it has not done so yet
		if (!attachment.view[prop]) {
			const ob$ = this.fbCloudFunctionsService.getFileUrl$(blob, environment.firebase.attachmentBucket);

			ob$.subscribe((url) => {
				this.attachments.localUpdate(attachment.$id, { [prop]: url });
				this.renderTrigger$.next(attachment.$id);
			});
		}
	}

	/**
	 * destroy
	 */
	public destroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.renderTrigger$.complete();
		this.file$.complete();
	}
}
